import { LinearProgress } from '@mui/material'
import BackComponent from 'components/back'
import ButtonMobileComponent from 'components/button-mobile'
import CheckboxComponent from 'components/checkbox'
import InputMobileComponent from 'components/input-mobile'
import MapPinComponent from 'components/map-pin'
import PlacesAutocompleteComponent from 'components/places-autocomplete'
import TextAreaComponent from 'components/textarea'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import Arrow from 'images/arrow_forward.png'
import { useState } from 'react'
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom'
import { LocationProps, MessageProps } from 'types/CommonTypes'
import { VehicleProps } from 'types/CommunityTypes'
import { StudentProps } from 'types/StudentTypes'
import { commentTooltip, isPaidTransportFeatureEnabled, isWeb } from 'utils/constants'
import { geocode } from 'utils/map'
import { updateStudentMorningSettings } from 'utils/students'
import { isValidComments } from 'utils/validations'
import './index.css'
interface ArrivalCarpoolRequestComponentProps {
  student: StudentProps
  onUpdate: (cb?: () => void) => void
}

const ArrivalCarpoolRequestComponent = (props: ArrivalCarpoolRequestComponentProps) => {
  const navigate: NavigateFunction = useNavigate()

  // Inputs: School Name, Location, Student Pick up Location and onUpdate Callbck
  const { student, onUpdate } = props
  const { schoolName, schoolLocation, studentId, pickupLocation } = student
  const { lat, lng } = schoolLocation
  const bounds = lat && lng ? [lng - 0.7, lat - 0.7, lng + 0.7, lat + 0.7] : undefined

  // states
  const [studentPickupLocation, setStudentPickupLocation] = useState<LocationProps>({
    address: pickupLocation?.address || '',
  })
  const [pickupVehicle, setPickupVehicle] = useState<VehicleProps>(student?.pickupVehicle ?? 0)
  const [pickupSeatsFilled, setPickupSeatsFilled] = useState<number | null>(student?.pickupSeatsFilled ?? null)
  const [pickupSeatsAvailable, setPickupSeatsAvailable] = useState<number | null>(student?.pickupSeatsAvailable ?? null)
  const [pickupComments, setPickupComments] = useState<string>(student?.pickupComments ?? '')
  const [msg, setMsg] = useState<MessageProps>()
  const [isLoading, setIsLoading] = useState<string>('')
  const [isFullPage, setIsFullPage] = useState(false)
  const [key, setKey] = useState<number>(0)

  // handlers
  const onSubmit = async (): Promise<any> => {
    setIsLoading('Saving...')
    let pickupLocation: LocationProps = studentPickupLocation
    if (pickupLocation.address && (!pickupLocation?.lat || !pickupLocation?.lng)) {
      pickupLocation = await geocode(pickupLocation.address)
      if (!pickupLocation?.lat || !pickupLocation?.lng) {
        setMsg({
          style: 'error',
          text: 'We could not locate this address. Verify and re-enter address.',
        })
        setIsLoading('')
        return
      }
    }
    const { msg, error } = await updateStudentMorningSettings(
      schoolName,
      studentId,
      pickupLocation,
      pickupVehicle,
      pickupSeatsFilled,
      pickupSeatsAvailable,
      pickupComments,
    )
    if (error) setMsg(msg)
    else onUpdate(() => navigate(-1))
    setIsLoading('')
  }

  // handlers
  const onCancel = () => navigate(-1)

  const handleToggleFullPage = () => {
    setIsFullPage((prev) => !prev)
  }

  const handleMapPinSave = (v: LocationProps) => {
    setStudentPickupLocation(v)
    setKey((prevKey) => prevKey + 1) // Changing the key forces a re-render
    handleToggleFullPage()
  }

  const isValid: boolean = isValidComments(pickupComments) && studentPickupLocation.address.trim().length > 0

  const body: JSX.Element = (
    <div className={isWeb ? 'm-1' : 'flex p-4 px-5 flex-col items-start gap-3 flex-[1_0_0] self-stretch'}>
      <PlacesAutocompleteComponent
        key={key}
        label='Select Address / Landmark'
        defaultValue={studentPickupLocation.address}
        bounds={bounds}
        onChange={(v: string) => setStudentPickupLocation({ address: v })}
        onSubmit={(v: LocationProps) => setStudentPickupLocation(v)}
        onMapClick={handleToggleFullPage}
        dataTestid='pickup-morning-testid'
      />
      {/***************** Map *****************/}
      <MapPinComponent
        isShow={isFullPage}
        currentLocation={pickupLocation?.address ? pickupLocation : schoolLocation}
        onClose={handleToggleFullPage}
        onSave={handleMapPinSave}
      />
      {/* Select vehicle */}
      <div className='w-full mt-1 mb-3' data-testid='vehicle-testid'>
        <label className='text-gray-400 text-sm font-medium'>Vehicle</label>
        {isPaidTransportFeatureEnabled && (
          <CheckboxComponent
            isChecked={!!(pickupVehicle & VehicleProps.HIRE_TRANSPORT_SERVICE)}
            onClick={() => setPickupVehicle(pickupVehicle ^ VehicleProps.HIRE_TRANSPORT_SERVICE)}
            label='Connect me with other users using or seeking paid transport'
          />
        )}
        <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
          <InputMobileComponent
            labeltext='Filled Seats'
            type='number'
            max={7} // Maximum value allowed
            min={1} // Minimum value allowed
            value={pickupSeatsFilled !== null ? pickupSeatsFilled : ''}
            onChange={(v: any) => setPickupSeatsFilled(Number(v))}
          />
          <InputMobileComponent
            labeltext='Available Seats'
            type='number'
            max={7} // Maximum value allowed
            min={1} // Minimum value allowed
            value={pickupSeatsAvailable !== null ? pickupSeatsAvailable : ''}
            onChange={(v: any) => setPickupSeatsAvailable(Number(v))}
          />
        </div>
      </div>
      {/* Add comments */}
      <div className='w-full mt-1 mb-3'>
        <TextAreaComponent
          labeltext='Comments'
          placeholder='Enter details to help with carpooling'
          isRequired={false}
          isError={!isValidComments(pickupComments)}
          helperText={!isValidComments(pickupComments) ? commentTooltip() : ''}
          value={pickupComments}
          onChange={(v: string) => setPickupComments(v)}
        />
      </div>
    </div>
  )
  const footer: JSX.Element = (
    <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-3 pb-10 px-5'>
      <ButtonMobileComponent
        id='find-school-button'
        data_testid='modal-dialog-action-submit'
        text='Submit'
        color='bg-custom-yellow'
        width='w-full'
        disabled={!isValid}
        onClick={onSubmit}
        iconPosition='right'
        icon={Arrow}
      />
    </div>
  )

  return (
    <>
      {/***************** Display progress and error *****************/}
      {isLoading && <LinearProgress />}
      {msg && (
        <ToastComponent style={msg?.style} heading={msg?.heading} text={msg?.text} onClose={() => setMsg(undefined)} />
      )}
      {/***************** Mobile *****************/}
      {!isWeb && (
        <div className='flex flex-col min-h-screen'>
          <div className='sticky top-0 z-10 bg-white'>
            <BackComponent text='Morning Carpool Request' onClick={onCancel} />
          </div>
          <div className='flex-1 pb-32'>{body}</div>
          {footer}
        </div>
      )}
      {/***************** Web *****************/}
      {isWeb && (
        <WizardComponent
          onBack={onCancel}
          onCancel={onCancel}
          title={'Morning Carpool Request'}
          disable={!isValid}
          onClick={onSubmit}
          btnText='Submit'
          data_testId='modal-dialog-action-submit'
        >
          {body}
        </WizardComponent>
      )}
    </>
  )
}

function Index(props: { students: StudentProps[]; onUpdate: (cb?: () => void) => void }): JSX.Element {
  const [searchParams] = useSearchParams()

  // Inputs: Selected Student Id, and Callback
  const { students, onUpdate } = props

  // Build props for child component
  const studentId = searchParams.get('id') ?? ''
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)

  if (!studentId || !student) return <LinearProgress />
  return <ArrivalCarpoolRequestComponent student={student} onUpdate={onUpdate} />
}

export default Index
